import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './components/LoginPage';
import LandingPage from './components/landing/LandingPage';
import ChangePasswordModal from './components/ChangePasswordModal';
import App from './App';
import { Loader2 } from 'lucide-react';

// Componente para manejar el callback de Google OAuth
const AuthCallback = () => {
  const { isAuthenticated } = useAuth();
  const [processing, setProcessing] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleCallback = async () => {
      const hash = window.location.hash;
      
      // Caso 1: Token recibido del OAuth callback
      const tokenMatch = hash.match(/token=([^&]+)/);
      if (tokenMatch) {
        const token = tokenMatch[1];
        console.log('[AuthCallback] Token received from OAuth callback');
        
        // Guardar token en localStorage
        localStorage.setItem('auth_token', token);
        
        // Limpiar el hash de la URL
        window.history.replaceState(null, '', window.location.pathname);
        
        // Recargar la página para que AuthContext verifique el token
        window.location.reload();
        return;
      }
      
      // Caso 2: Error recibido del OAuth callback
      const errorMatch = hash.match(/error=([^&]+)/);
      if (errorMatch) {
        const errorMsg = decodeURIComponent(errorMatch[1]);
        console.log('[AuthCallback] Error from OAuth:', errorMsg);
        setError(errorMsg);
        
        // Limpiar el hash
        window.history.replaceState(null, '', window.location.pathname);
        setProcessing(false);
        return;
      }
      
      // No hay token ni error en el hash
      setProcessing(false);
    };

    handleCallback();
  }, []);

  // Si hay error, mostrar en LoginPage directamente (evita un reload extra pasando por "/")
  if (error) {
    // Pasar el error al LoginPage via localStorage temporalmente
    localStorage.setItem('oauth_error', error);
    window.location.href = '/login';
    return null;
  }

  // Si ya está autenticado después del callback
  if (isAuthenticated) {
    return null; // AuthenticatedApp manejará la redirección
  }

  // Procesando callback
  if (processing) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-slate-600">Verificando con Google...</p>
        </div>
      </div>
    );
  }

  // Si no hay token y no está autenticado, mostrar login
  return <LoginPage />;
};

// Ruta pública "/" - landing de producto. Si hay callback de Google en el hash, se delega
// a AuthCallback (sin tocar su lógica). Si ya hay sesión (o vino un error de SSO), redirige
// para no mostrarle la landing a alguien que ya está logueado.
const PublicHome = () => {
  const { isAuthenticated, ssoError } = useAuth();
  const hash = window.location.hash;
  const hasOAuthCallback = hash?.includes('token=') || hash?.includes('error=');

  if (hasOAuthCallback) {
    return <AuthCallback />;
  }
  if (ssoError) {
    return <Navigate to="/login" replace />;
  }
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  return <LandingPage />;
};

// Ruta "/login" - mismo LoginPage de siempre, sin rediseño. Si ya está logueado, al panel.
const LoginRoute = () => {
  const { isAuthenticated } = useAuth();
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  return <LoginPage />;
};

// Ruta protegida "/dashboard" - mismo guard de siempre: sin sesión, a /login.
const DashboardRoute = () => {
  const { user, isAuthenticated, logout } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (user?.must_change_password) {
    return <ChangePasswordModal />;
  }
  return <App authUser={user} onLogout={logout} />;
};

const AuthenticatedApp = () => {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-slate-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<PublicHome />} />
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/dashboard" element={<DashboardRoute />} />
      {/* Cualquier otra ruta: nunca expone nada protegido, vuelve a la home pública */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const AppWithAuth = () => {
  return (
    <AuthProvider>
      <AuthenticatedApp />
    </AuthProvider>
  );
};

export default AppWithAuth;
